import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import en from '../locales/en.json';
import uz from '../locales/uz.json';
import ru from '../locales/ru.json';

export type Locale = 'uz' | 'en' | 'ru';
export const LOCALES: { code: Locale; label: string; flag: string }[] = [
  { code: 'uz', label: 'O‘zbekcha', flag: '🇺🇿' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
];

const DICT: Record<Locale, Record<string, string>> = { en, uz, ru };

const intlLocale: Record<Locale, string> = { en: 'en', uz: 'uz-UZ', ru: 'ru' };

interface LocaleCtx {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
  formatDate: (d: string | Date | null | undefined) => string;
  formatNumber: (n: number) => string;
  relativeTime: (d: string | Date | null | undefined) => string;
}
const Ctx = createContext<LocaleCtx | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() =>
    typeof window !== 'undefined' ? ((localStorage.getItem('lb-locale') as Locale) ?? 'uz') : 'uz',
  );
  const setLocale = (l: Locale) => {
    setLocaleState(l);
    if (typeof window !== 'undefined') localStorage.setItem('lb-locale', l);
  };

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => {
      let s = DICT[locale]?.[key] ?? DICT.en[key] ?? key;
      if (vars)
        for (const [k, v] of Object.entries(vars))
          s = s.replace(new RegExp(`{{\\s*${k}\\s*}}`, 'g'), String(v));
      return s;
    },
    [locale],
  );

  const value = useMemo<LocaleCtx>(() => {
    const il = intlLocale[locale];
    const formatDate = (d: string | Date | null | undefined) => {
      if (!d) return '—';
      const date = typeof d === 'string' ? new Date(d) : d;
      return date.toLocaleString(il, { dateStyle: 'medium', timeStyle: 'short' });
    };
    const formatNumber = (n: number) => new Intl.NumberFormat(il).format(n);
    const rtf = new Intl.RelativeTimeFormat(il, { numeric: 'auto' });
    const relativeTime = (d: string | Date | null | undefined) => {
      if (!d) return '—';
      const date = typeof d === 'string' ? new Date(d) : d;
      const diffSec = Math.round((date.getTime() - Date.now()) / 1000);
      const abs = Math.abs(diffSec);
      if (abs < 60) return rtf.format(diffSec, 'second');
      const m = Math.round(diffSec / 60);
      if (Math.abs(m) < 60) return rtf.format(m, 'minute');
      const h = Math.round(m / 60);
      if (Math.abs(h) < 24) return rtf.format(h, 'hour');
      const day = Math.round(h / 24);
      return rtf.format(day, 'day');
    };
    return { locale, setLocale, t, formatDate, formatNumber, relativeTime };
  }, [locale, t]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLocale(): LocaleCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useLocale must be used within LocaleProvider');
  return v;
}
